import React, { ChangeEvent } from 'react'
import { NameDto } from '@/api/Common/Types'
import { InterestRateEligibilityCriteriaDto } from '@/api/Interest-Rates/InterestRateDto'
import { CurrencyType } from '@/api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from '@/api/Wallet/Types/WalletCurrencyDto'
import { Button } from '@/components/ui/button'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

import { InterestRateEligibilityCriteriaType } from './AddInterestRateEligibilityForm'

export interface EditInterestRateEligibilityFormValues {
  id: string
  interestRateEligibilityCriteriaType: NameDto | null
  value: string | boolean
}

const EditInterestRateEligibilityFormUI: React.FC<
  FormikProps<EditInterestRateEligibilityFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors, values, currencies } = props
  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Eligibility Criteria</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const valueId = parseInt(e.target.value, 10)
                  const type = props.types.find((t) => t.id === valueId)
                  if (type?.id === InterestRateEligibilityCriteriaType.SwapFree) {
                    props.setValues({
                      interestRateEligibilityCriteriaType: type ?? null,
                      value: true,
                      id: props.values.id,
                    })
                  } else if (
                    type?.id === InterestRateEligibilityCriteriaType.MinimumAmountCurrency
                  ) {
                    props.setValues({
                      interestRateEligibilityCriteriaType: type,
                      value: currencies[0].id,
                      id: props.values.id,
                    })
                  } else {
                    props.setFieldValue('interestRateEligibilityCriteriaType', type ?? null)
                  }
                }}
                component='select'
                name='interestRateEligibilityCriteriaType.id'
              >
                <option value='' disabled>
                  None
                </option>
                {(props.types || []).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.interestRateEligibilityCriteriaType && (
            <FormBaseErrorMessage>
              {errors.interestRateEligibilityCriteriaType}
            </FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Value</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              {values.interestRateEligibilityCriteriaType?.id ===
                InterestRateEligibilityCriteriaType.ActiveTradingDays && (
                <Field type='number' name='value' />
              )}
              {values.interestRateEligibilityCriteriaType?.id ===
                InterestRateEligibilityCriteriaType.OpenPositions && (
                <Field type='number' name='value' />
              )}
              {values.interestRateEligibilityCriteriaType?.id ===
                InterestRateEligibilityCriteriaType.Deposit && <Field type='number' name='value' />}
              {values.interestRateEligibilityCriteriaType?.id ===
                InterestRateEligibilityCriteriaType.MinimumAmountCurrency && (
                <Field
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    const currTypeId = e.target.value as CurrencyType
                    const currency = props.currencies.find((t) => t.id === currTypeId)
                    props.setFieldValue('value', currency?.id ?? null)
                  }}
                  component='select'
                  name='value'
                >
                  <option value='' disabled>
                    None
                  </option>
                  {(props.currencies || []).map((curr) => (
                    <option key={curr.id} value={curr.id}>
                      {curr.id}
                    </option>
                  ))}
                </Field>
              )}
              {values.interestRateEligibilityCriteriaType?.id ===
                InterestRateEligibilityCriteriaType.SwapFree && (
                <Field type='checkbox' name='value' checked={Boolean(values.value)} />
              )}
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.value && <FormBaseErrorMessage>{errors.value}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' onClick={handleSubmitForm} type='button'>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  currencies: WalletCurrencyDto[]
  data: InterestRateEligibilityCriteriaDto
  permissions: PermissionDto[]
  types: NameDto[]

  onSubmit(values: FormSubmitValues<EditInterestRateEligibilityFormValues>): Promise<void>
}

export const EditInterestRateEligibilityForm = withFormik<
  OuterProps,
  EditInterestRateEligibilityFormValues
>({
  mapPropsToValues: ({ data }) => {
    return {
      id: data.id,
      interestRateEligibilityCriteriaType: data.eligibilityCriteriaType,
      value: data.value,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<EditInterestRateEligibilityFormValues> = {}
    if (!values.interestRateEligibilityCriteriaType) {
      errors.interestRateEligibilityCriteriaType = 'required'
    }
    if (
      values.interestRateEligibilityCriteriaType?.id ===
        InterestRateEligibilityCriteriaType.ActiveTradingDays &&
      !values.value
    ) {
      errors.value = 'required'
    }
    if (
      values.interestRateEligibilityCriteriaType?.id ===
        InterestRateEligibilityCriteriaType.OpenPositions &&
      !values.value
    ) {
      errors.value = 'required'
    }
    if (
      values.interestRateEligibilityCriteriaType?.id ===
        InterestRateEligibilityCriteriaType.Deposit &&
      !values.value
    ) {
      errors.value = 'required'
    }
    if (
      values.interestRateEligibilityCriteriaType?.id ===
        InterestRateEligibilityCriteriaType.MinimumAmountCurrency &&
      !values.value
    ) {
      errors.value = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(EditInterestRateEligibilityFormUI)
